import { z } from 'zod';
import { emailSchema } from './common';

export const loginSchema = z.object({
  email: emailSchema,
  password: z
    .string({ message: 'Enter your password' })
    .min(8, { message: 'Password must be at least 8 characters' }),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * The rule for CHOOSING a password, which is deliberately stricter than the
 * rule for TYPING an existing one.
 *
 * `loginSchema` above still accepts 8 characters and must keep doing so: it
 * validates a password that was already accepted at some point in the past, and
 * tightening it would lock out every existing account with an 8-character
 * password — the form would reject them before the server ever got a chance to
 * say whether the credential was right.
 *
 * The upper bound is not cosmetic. PBKDF2 runs over the whole input, so an
 * unbounded password field is an unauthenticated request that costs the worker
 * whatever the caller decides to spend. Both places that set a password are
 * public endpoints, so the cap is load-bearing.
 */
export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 200;

/**
 * Rejected outright regardless of length. A token gesture against the very
 * worst choices, not a substitute for a breach corpus — it exists because
 * "password123" clears a bare 10-character minimum.
 */
const OBVIOUS_PASSWORDS = new Set([
  'password',
  'password1',
  'password12',
  'password123',
  'password1234',
  '1234567890',
  '12345678901',
  '123456789012',
  'qwertyuiop',
  'qwerty12345',
  'letmein123',
  'iloveyou123',
  'administrator',
  'lawncare123',
  'ballandchaney',
]);

export const newPasswordSchema = z
  .string({ message: 'Choose a password' })
  .min(MIN_PASSWORD_LENGTH, {
    message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
  })
  .max(MAX_PASSWORD_LENGTH, {
    message: `Password must be at most ${MAX_PASSWORD_LENGTH} characters`,
  })
  .refine((value) => !OBVIOUS_PASSWORDS.has(value.toLowerCase()), {
    message: 'That password is too easy to guess. Choose another.',
  });

/**
 * Accepting an invite. The token travels in the URL, never the body, so it is
 * not part of this schema.
 */
export const acceptInviteSchema = z.object({
  password: newPasswordSchema,
});

export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>;

/**
 * Public registration, which creates a NEW organization and its first OWNER.
 *
 * This never joins an existing organization. That distinction is the entire
 * security model of the endpoint: a stranger who registers gets an empty
 * business of their own, and there is no field here — or anywhere in the
 * handler — that can point the new user at somebody else's data.
 */
export const registerSchema = z.object({
  organizationName: z
    .string({ message: 'Enter your business name' })
    .trim()
    .min(2, { message: 'Enter your business name' })
    .max(120, { message: 'Business name is too long' }),
  displayName: z
    .string({ message: 'Enter your name' })
    .trim()
    .min(1, { message: 'Enter your name' })
    .max(120, { message: 'Name is too long' }),
  email: emailSchema,
  password: newPasswordSchema,
});

export type RegisterInput = z.infer<typeof registerSchema>;
